import { Feedback, ParentChildFeedback, ResourceRequest } from "../Models/Feedback";
import {
  FeedbackCategory,
  FeedbackState,
  FeedbackEditType,
  FeedbackParent
} from "../Constants/feedbackEnums";
import { FeatureRequest, BugReport } from "..";
import { UpdateFeedback } from "../Models/UpdateFeedback";
import { appPaths } from "../../Common";
import { FeedbackAutocompleteItem } from "../../Layout/Models/AutocompleteItem";
import { Application } from "../../App";
import { Topic } from "../../Topic/Models/Topic";

export class FeedbackService {
  createSubmitModel = (
    feedback: Feedback,
    editType: FeedbackEditType = FeedbackEditType.Normal
  ): UpdateFeedback => {
    return {
      title: feedback.title,
      feedbackParent: feedback.parent,
      appRolePrefix: feedback.application ? feedback.application.rolePrefix : null,
      topicCode: feedback.topic ? feedback.topic.code : null,
      feedbackCategory: FeedbackCategory[feedback.category as keyof typeof FeedbackCategory],
      feedbackState: FeedbackState[feedback.state as keyof typeof FeedbackState],
      imageRefs: feedback.images,
      files: feedback.files,
      editType,
      priority: feedback.priority,
      areCommentsEnabled: feedback.areCommentsEnabled,
      tagIds: feedback.tagIds,
      isPrivate: feedback.isPrivate,
      dueDate: feedback.dueDate ? new Date(feedback.dueDate) : undefined,
      ...this.createFeatureRequestModel(feedback),
      ...this.createBugReportModel(feedback),
      ...this.createResourceRequestModel(feedback)
    };
  };

  getViewUrl = (feedback: Feedback): string => {
    let url = "";

    switch (feedback.parent) {
      case FeedbackParent.Application:
        url = this.getViewUrlForCodeAndAppRolePrefix(
          feedback.code,
          feedback.application.rolePrefix,
          feedback.category
        );
        break;
      case FeedbackParent.Topic:
        url = this.getViewUrlForCodeAndTopicCode(
          feedback.code,
          feedback.topic.code,
          feedback.category
        );
        break;
      default:
        throw new Error(`Could not get view url for feedback with parent: ${feedback.parent}`);
    }

    return url;
  };

  getAutocompleteViewUrl = (feedback: FeedbackAutocompleteItem): string => {
    let url = "";
    switch (feedback.parent) {
      case FeedbackParent.Application:
        url = this.getViewUrlForCodeAndAppRolePrefix(
          feedback.code,
          feedback.appRolePrefix as string,
          feedback.category
        );
        break;
      case FeedbackParent.Topic:
        url = this.getViewUrlForCodeAndTopicCode(
          feedback.code,
          feedback.topicCode as string,
          feedback.category
        );
        break;
      default:
        throw new Error(`Could not get view url for feedback with parent: ${feedback.parent}`);
    }

    return url;
  };

  getParentChildViewUrl = (feedback: ParentChildFeedback): string => {
    let url = "";

    switch (feedback.parent) {
      case FeedbackParent.Application:
        url = this.getViewUrlForCodeAndAppRolePrefix(
          feedback.code,
          feedback.appPrefix,
          feedback.category
        );
        break;
      case FeedbackParent.Topic:
        url = this.getViewUrlForCodeAndTopicCode(
          feedback.code,
          feedback.topicName,
          feedback.category
        );
        break;
      default:
        throw new Error(`Could not get view url for feedback with parent: ${feedback.parent}`);
    }

    return url;
  };

  getViewUrlForCodeAndAppRolePrefix = (
    feedbackCode: string,
    appRolePrefix: string,
    category: FeedbackCategory
  ): string => {
    let basePath: string;
    switch (category) {
      case FeedbackCategory.FeatureRequest:
        basePath = appPaths.app.viewFeatureRequest;
        break;
      case FeedbackCategory.BugReport:
        basePath = appPaths.app.viewBugReport;
        break;
      case FeedbackCategory.ResourceRequest:
        basePath = appPaths.app.viewResourceRequest;
        break;
      default:
        throw new Error(`Unsupported category: ${category}`);
    }

    return basePath.replace(":id", appRolePrefix).replace(":code", feedbackCode);
  };

  getViewUrlForCodeAndTopicCode = (
    feedbackCode: string,
    topicCode: string,
    category: FeedbackCategory
  ): string => {
    let basePath: string;
    switch (category) {
      case FeedbackCategory.FeatureRequest:
        basePath = appPaths.topic.viewFeatureRequest;
        break;
      case FeedbackCategory.BugReport:
        basePath = appPaths.topic.viewBugReport;
        break;
      case FeedbackCategory.ResourceRequest:
        basePath = appPaths.topic.viewResourceRequest;
        break;
      default:
        throw new Error(`Unsupported category: ${category}`);
    }

    return basePath.replace(":topicCode", topicCode).replace(":code", feedbackCode);
  };

  private createFeatureRequestModel = (feedback: Feedback) => {
    const featureRequest = feedback as FeatureRequest;

    return {
      description: featureRequest.description,
      featureRequirements: featureRequest.featureRequirements,
      startDate: featureRequest.startDate,
      estimatedHours: featureRequest.estimatedHours,
      feedbackSize: featureRequest.feedbackSize
    };
  };

  private createBugReportModel = (feedback: Feedback) => {
    const bugReport = feedback as BugReport;

    return {
      reproductionSteps: bugReport.reproductionSteps,
      description: bugReport.description,
      expectedBehaviour: bugReport.expectedBehaviour,
      link: bugReport.link,
      startDate: bugReport.startDate,
      estimatedHours: bugReport.estimatedHours,
      browserName: bugReport.browserName,
      browserVersion: bugReport.browserVersion
    };
  };

  private createResourceRequestModel = (feedback: Feedback) => {
    const resourceRequest = feedback as ResourceRequest;

    return {
      description: resourceRequest.description,
      categoryName: resourceRequest.categoryName,
      specSheetLink: resourceRequest.specSheetLink
    };
  };
}

export const feedbackService = new FeedbackService();
